/**
 * Poppedijn Theme - Core JavaScript
 * Minimal, no frameworks, only what's needed.
 */

(function () {
  'use strict';

  /* ========================================
     Helpers
     ======================================== */
  function escHtml(str) {
    var d = document.createElement('div');
    d.textContent = str;
    return d.innerHTML;
  }

  /* ── Body scroll-lock (iOS-veilig) ──
     `body { overflow: hidden }` stopt touch-scroll op iOS Safari niet; de pagina
     achter een overlay blijft scrollen. Daarom pinnen we de body met
     position:fixed op de huidige scrollpositie en zetten die bij ontgrendelen
     exact terug (anders kom je op een andere plek terug). Idempotent: meerdere
     keren locken/ontgrendelen kan geen kwaad. Gedeeld door alle overlays
     (mobiel menu, cart-drawer, zoek-overlays, variant-picker). */
  var _scrollLocked = false;
  var _scrollLockY = 0;
  function lockBodyScroll() {
    if (_scrollLocked) return;
    _scrollLocked = true;
    _scrollLockY = window.scrollY || window.pageYOffset || 0;
    var b = document.body;
    b.style.position = 'fixed';
    b.style.top = (-_scrollLockY) + 'px';
    b.style.left = '0';
    b.style.right = '0';
    b.style.width = '100%';
    b.style.overflow = 'hidden';
  }
  function unlockBodyScroll() {
    if (!_scrollLocked) return;
    _scrollLocked = false;
    var b = document.body;
    b.style.position = '';
    b.style.top = '';
    b.style.left = '';
    b.style.right = '';
    b.style.width = '';
    b.style.overflow = '';
    window.scrollTo(0, _scrollLockY);
  }

  /* ── Focus trap voor drawers/modals (WCAG 2.4.3) ──
     Houdt Tab binnen het element, onthoudt waar de focus vandaan kwam
     en zet hem daar terug bij sluiten. Gebruik: var release = trapFocus(el); later release(); */
  function trapFocus(container) {
    var previouslyFocused = document.activeElement;
    var selector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

    function onKeydown(e) {
      if (e.key !== 'Tab') return;
      var focusables = Array.prototype.filter.call(
        container.querySelectorAll(selector),
        function (el) { return el.offsetParent !== null; }
      );
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    container.addEventListener('keydown', onKeydown);
    /* Focus naar het eerste interactieve element (meestal de sluitknop) */
    var firstEl = container.querySelector(selector);
    if (firstEl) firstEl.focus();

    return function release() {
      container.removeEventListener('keydown', onKeydown);
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
  }

  /* Cart-add respons afhandelen: Shopify geeft bij voorraad/variant-problemen
     een 4xx met een nette uitleg in .description. Die werpen we als fout op
     zodat de aanroeper NIET ten onrechte "toegevoegd" toont. */
  function handleAddResponse(r) {
    return r.json().then(function (data) {
      if (!r.ok) {
        var msg = (data && (data.description || data.message)) || 'Dit lukte niet, het product is mogelijk net uitverkocht.';
        var err = new Error(msg);
        err.handled = true;
        throw err;
      }
      return data;
    });
  }

  function showCartToast(message, isError) {
    var existing = document.querySelector('.cart-toast');
    if (existing) existing.remove();
    var toast = document.createElement('div');
    toast.className = 'cart-toast' + (isError ? ' cart-toast--error' : '');
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.textContent = message;
    document.body.appendChild(toast);
    requestAnimationFrame(function () { toast.classList.add('is-visible'); });
    setTimeout(function () {
      toast.classList.remove('is-visible');
      setTimeout(function () { toast.remove(); }, 300);
    }, 3000);
  }

  /* ========================================
     Mobile menu toggle
     ======================================== */
  const mobileMenuToggle = document.querySelector('[data-mobile-menu-toggle]');
  const mobileMenu = document.querySelector('[data-mobile-menu]');
  const mobileMenuClose = document.querySelector('[data-mobile-menu-close]');
  const overlay = document.querySelector('[data-overlay]');

  var releaseMenuTrap = null;

  function openMobileMenu() {
    if (!mobileMenu) return;
    mobileMenu.classList.add('is-open');
    lockBodyScroll();
    if (overlay) overlay.classList.add('is-visible');
    mobileMenuToggle?.setAttribute('aria-expanded', 'true');
    releaseMenuTrap = trapFocus(mobileMenu);
  }

  function closeMobileMenu() {
    if (!mobileMenu) return;
    mobileMenu.classList.remove('is-open');
    unlockBodyScroll();
    if (overlay) overlay.classList.remove('is-visible');
    mobileMenuToggle?.setAttribute('aria-expanded', 'false');
    if (releaseMenuTrap) { releaseMenuTrap(); releaseMenuTrap = null; }
  }

  mobileMenuToggle?.addEventListener('click', openMobileMenu);
  mobileMenuClose?.addEventListener('click', closeMobileMenu);
  overlay?.addEventListener('click', function () {
    closeMobileMenu();
    closeCartDrawer();
    closeSearch();
  });

  /* ========================================
     Cart drawer
     ======================================== */
  const cartToggle = document.querySelectorAll('[data-cart-toggle]');
  const cartDrawer = document.querySelector('[data-cart-drawer]');
  const cartClose = document.querySelector('[data-cart-close]');

  var cartAutoCloseTimer = null;
  var CART_AUTO_CLOSE_MS = 8000;

  function startCartAutoClose() {
    clearTimeout(cartAutoCloseTimer);
    cartAutoCloseTimer = setTimeout(function () {
      closeCartDrawer();
    }, CART_AUTO_CLOSE_MS);
  }

  var releaseCartTrap = null;

  function openCartDrawer() {
    if (!cartDrawer) return;
    var wasOpen = cartDrawer.classList.contains('is-open');
    cartDrawer.classList.add('is-open');
    lockBodyScroll();
    if (overlay) overlay.classList.add('is-visible');
    startCartAutoClose();
    if (!wasOpen) releaseCartTrap = trapFocus(cartDrawer);
  }

  function refreshCartDrawer() {
    return fetch('/?sections=cart-drawer')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var html = data['cart-drawer'];
        if (!html) return;
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-cart-drawer]');
        if (fresh && cartDrawer) {
          cartDrawer.innerHTML = fresh.innerHTML;
          /* Re-bind close button */
          var newClose = cartDrawer.querySelector('[data-cart-close]');
          if (newClose) {
            newClose.addEventListener('click', closeCartDrawer);
            /* innerHTML-swap kan de focus naar body gooien; alleen dan herstellen
               (niet bij b.v. een plus/min-klik waar focus elders zinvol is) */
            if (cartDrawer.classList.contains('is-open') && document.activeElement === document.body) {
              newClose.focus();
            }
          }
        }
      })
      .then(function () { loadCartRecommendations(); })
      .then(function () { return refreshCartPageItems(); })
      .then(function () { syncDiscountMeta(); })
      .catch(function () {});
  }

  /* ── Regel-lijst op de winkelwagenpagina live houden ──
     De lijst wordt server-side gerenderd bij het laden; elke AJAX-wijziging
     (toevoegen via aanbevelingen, aantal/verwijderen in de drawer, cadeau
     inpakken) ververst hem hier opnieuw vanaf de server. Zo tonen de regels
     ALTIJD exact wat er in de winkelwagen-sessie zit en kunnen lijst en
     totalen nooit uit elkaar lopen. */
  function refreshCartPageItems() {
    var list = document.querySelector('[data-cart-items]');
    if (!list) return Promise.resolve();
    var sectionEl = list.closest('.shopify-section');
    if (!sectionEl || !sectionEl.id) return Promise.resolve();
    var sectionId = sectionEl.id.replace(/^shopify-section-/, '');
    return fetch('/cart?sections=' + encodeURIComponent(sectionId))
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var html = data[sectionId];
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-cart-items]');
        if (!fresh) {
          /* Winkelwagen is leeg geworden: de hele lay-out verandert */
          window.location.reload();
          return;
        }
        list.innerHTML = fresh.innerHTML;
        /* Totalen uit dezelfde server-render meenemen, zodat regels en
           bedragen altijd uit een en dezelfde winkelwagen-staat komen */
        var totals = document.querySelector('[data-cart-totals]');
        var freshTotals = doc.querySelector('[data-cart-totals]');
        if (totals && freshTotals) totals.innerHTML = freshTotals.innerHTML;
      })
      .catch(function () {});
  }

  function closeCartDrawer() {
    if (!cartDrawer) return;
    cartDrawer.classList.remove('is-open');
    unlockBodyScroll();
    if (overlay) overlay.classList.remove('is-visible');
    if (releaseCartTrap) { releaseCartTrap(); releaseCartTrap = null; }
  }

  /* Pause auto-close zodra de gebruiker met de drawer bezig is.
     Elke interactie (muis, touch, toetsenbord, focus, klik) annuleert de
     timer definitief; alleen muis-verlaten start hem opnieuw. */
  if (cartDrawer) {
    cartDrawer.addEventListener('mouseenter', function () {
      clearTimeout(cartAutoCloseTimer);
    });
    cartDrawer.addEventListener('mouseleave', function () {
      if (cartDrawer.classList.contains('is-open')) startCartAutoClose();
    });
    cartDrawer.addEventListener('touchstart', function () {
      clearTimeout(cartAutoCloseTimer);
    }, { passive: true });
    cartDrawer.addEventListener('keydown', function () {
      clearTimeout(cartAutoCloseTimer);
    });
    cartDrawer.addEventListener('focusin', function () {
      clearTimeout(cartAutoCloseTimer);
    });
    cartDrawer.addEventListener('click', function () {
      clearTimeout(cartAutoCloseTimer);
    });
  }

  cartToggle.forEach(function (btn) {
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      refreshCartDrawer();
      openCartDrawer();
      /* Dismiss the cart-reminder banner when user opens the cart */
      var reminder = document.querySelector('[data-cart-reminder]');
      if (reminder) {
        reminder.classList.remove('is-visible');
        reminder.classList.add('is-hidden');
        try { localStorage.setItem('cart_reminder_dismissed_at', String(Date.now())); } catch (e) { /* private mode */ }
      }
    });
  });
  cartClose?.addEventListener('click', closeCartDrawer);

  /* Listen for cart:open from other scripts (e.g. wishlist claim) */
  document.addEventListener('cart:open', function () {
    refreshCartDrawer();
    openCartDrawer();
  });

  /* Listen for cart:updated from other scripts (e.g. cart bundle, wishlist).
     Gebruikt e.detail als die een volledige cart bevat, anders één centrale fetch.
     Ververst daarnaast altijd de regel-lijst op de winkelwagenpagina en de
     korting-bedragen, zodat alles wat de bezoeker ziet uit dezelfde verse
     winkelwagen-staat komt. */
  document.addEventListener('cart:updated', function (e) {
    var detail = e && e.detail;
    if (detail && typeof detail.item_count === 'number' && typeof detail.total_price === 'number') {
      applyCartState(detail);
    } else {
      updateCartUI(true);
    }
    refreshCartPageItems().then(function () { syncDiscountMeta(); });
  });

  /* ========================================
     Search toggle
     ======================================== */
  const searchToggle = document.querySelector('[data-search-toggle]');
  const searchOverlay = document.querySelector('[data-search-overlay]');
  const searchClose = document.querySelector('[data-search-close]');
  const searchInput = document.querySelector('[data-search-input]');

  function openSearch() {
    if (!searchOverlay) return;
    searchOverlay.classList.add('is-open');
    lockBodyScroll();
    searchInput?.focus();
  }

  function closeSearch() {
    if (!searchOverlay) return;
    searchOverlay.classList.remove('is-open');
    unlockBodyScroll();
  }

  searchToggle?.addEventListener('click', openSearch);
  searchClose?.addEventListener('click', closeSearch);

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      closeMobileMenu();
      closeCartDrawer();
      closeSearch();
    }
  });

  /* ========================================
     Centrale cart-state
     E\u00E9n /cart.js fetch met in-flight dedupe:
     parallelle aanroepen delen dezelfde promise,
     zodat count, verzendbalk en reminder nooit
     uit de pas lopen (race condition fix).
     ======================================== */
  var _cartFetch = null;
  function fetchCart(force) {
    /* force: na een cart-mutatie mag een oudere in-flight fetch niet hergebruikt worden */
    if (_cartFetch && !force) return _cartFetch;
    _cartFetch = fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('cart fetch failed: ' + r.status);
        return r.json();
      })
      .then(function (cart) { _cartFetch = null; return cart; })
      .catch(function (err) { _cartFetch = null; throw err; });
    return _cartFetch;
  }

  /* Past alle cart-afhankelijke UI in \u00E9\u00E9n keer toe op basis van \u00E9\u00E9n cart-object */
  function applyCartState(cart) {
    if (!cart || typeof cart.item_count !== 'number') return;

    /* Cart count badges */
    document.querySelectorAll('[data-cart-count]').forEach(function (badge) {
      badge.textContent = cart.item_count;
      badge.classList.toggle('is-hidden', cart.item_count === 0);
    });

    /* Free shipping progress bars */
    var bars = document.querySelectorAll('[data-free-shipping-bar]');
    if (bars.length && typeof cart.total_price === 'number') {
      var threshold = parseInt(
        document.documentElement.dataset.freeShippingThreshold || '6500',
        10
      );
      var total = cart.total_price;
      var remaining = threshold - total;
      var percent = Math.min((total / threshold) * 100, 100);
      bars.forEach(function (bar) {
        var fill = bar.querySelector('[data-shipping-fill]');
        var text = bar.querySelector('[data-shipping-text]');
        if (fill) fill.style.width = percent + '%';
        if (text) {
          if (remaining <= 0) {
            text.textContent = 'Je hebt gratis verzending!';
            bar.classList.add('is-free');
          } else {
            var euros = (remaining / 100).toFixed(2).replace('.', ',');
            text.textContent = 'Nog \u20AC' + euros + ' voor gratis verzending';
            bar.classList.remove('is-free');
          }
        }
      });
    }

    /* Cart reminder bar: alleen tekst bijwerken en verbergen bij lege cart.
       Tonen blijft de verantwoordelijkheid van de reminder-logica
       (terugkerende bezoeker + niet weggeklikt). */
    var reminder = document.querySelector('[data-cart-reminder]');
    if (reminder) {
      var countEl = reminder.querySelector('[data-cart-reminder-count]');
      var nounEl = reminder.querySelector('[data-cart-reminder-noun]');
      if (countEl) countEl.textContent = String(cart.item_count);
      if (nounEl) nounEl.textContent = (cart.item_count === 1 ? 'artikel' : 'artikelen');
      if (cart.item_count === 0) {
        reminder.classList.remove('is-visible');
      }
    }
  }

  /* E\u00E9n aanroep voor alle cart-UI updates */
  function updateCartUI(force) {
    return fetchCart(force).then(applyCartState).catch(function () { /* offline: huidige UI laten staan */ });
  }


  /* ========================================
     Add to cart (AJAX)
     ======================================== */
  document.addEventListener('submit', function (e) {
    var form = e.target.closest('form[action="/cart/add"]');
    if (!form) return;
    e.preventDefault();

    var formData = new FormData(form);
    var submitBtn = form.querySelector('[type="submit"]');
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = 'Toevoegen...';
    }

    fetch('/cart/add.js', {
      method: 'POST',
      body: formData,
    })
      .then(handleAddResponse)
      .then(function () {
        updateCartUI(true);
        refreshCartDrawer();
        openCartDrawer();
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = 'In winkelwagen';
        }
      })
      .catch(function (err) {
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = 'In winkelwagen';
        }
        showCartToast(err && err.handled ? err.message : 'Er ging iets mis bij het toevoegen. Probeer het opnieuw.', true);
      });
  });

  /* ========================================
     Quick-add to cart (product cards)
     Uses capture phase so it fires BEFORE the
     parent <a> link processes the click.
     ======================================== */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();

    var variantId = btn.dataset.quickAdd;
    if (!variantId) return;

    btn.classList.add('is-loading');
    var origHTML = btn.innerHTML;
    btn.innerHTML = '<span>Toevoegen...</span>';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] })
    })
      .then(handleAddResponse)
      .then(function () {
        updateCartUI(true);
        refreshCartDrawer();
        openCartDrawer();
        btn.innerHTML = origHTML;
        btn.classList.remove('is-loading');
      })
      .catch(function (err) {
        btn.innerHTML = origHTML;
        btn.classList.remove('is-loading');
        showCartToast(err && err.handled ? err.message : 'Er ging iets mis bij het toevoegen. Probeer het opnieuw.', true);
      });
  }, true);

  /* ========================================
     Quick-add met varianten: kleine kiezer
     bij "Kies optie" op productkaarten.
     ======================================== */
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-quick-options]');
    if (!trigger) return;
    e.preventDefault();
    e.stopPropagation();

    var handle = trigger.dataset.quickOptions;
    var existing = document.querySelector('.variant-picker');
    if (existing) existing.remove();

    fetch('/products/' + encodeURIComponent(handle) + '.js')
      .then(function (r) {
        if (!r.ok) throw new Error('product fetch failed');
        return r.json();
      })
      .then(function (product) {
        var picker = document.createElement('div');
        picker.className = 'variant-picker';
        picker.setAttribute('role', 'dialog');
        picker.setAttribute('aria-modal', 'true');
        picker.setAttribute('aria-label', 'Kies een optie');

        var optionsHtml = '';
        product.variants.forEach(function (v) {
          var prijs = '€' + (v.price / 100).toFixed(2).replace('.', ',');
          optionsHtml += '<option value="' + v.id + '"' + (v.available ? '' : ' disabled') + '>' +
            escHtml(v.title) + ' - ' + prijs + (v.available ? '' : ' (uitverkocht)') + '</option>';
        });

        picker.innerHTML =
          '<div class="variant-picker__overlay" data-vp-close></div>' +
          '<div class="variant-picker__modal">' +
            '<button type="button" class="variant-picker__close" data-vp-close aria-label="Sluiten">' +
              '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
            '</button>' +
            '<p class="variant-picker__title">' + escHtml(product.title) + '</p>' +
            '<label class="variant-picker__label" for="vp-select">Kies een optie</label>' +
            '<select id="vp-select" class="variant-picker__select">' + optionsHtml + '</select>' +
            '<button type="button" class="btn btn--primary btn--full" data-vp-add>In winkelwagen</button>' +
            '<a href="' + escHtml(product.url) + '" class="variant-picker__more">Bekijk alle details</a>' +
          '</div>';

        document.body.appendChild(picker);
        lockBodyScroll();
        var releasePickerTrap = trapFocus(picker.querySelector('.variant-picker__modal'));
        var select = picker.querySelector('#vp-select');
        select.focus();

        function closePicker() {
          releasePickerTrap();
          picker.remove();
          unlockBodyScroll();
          document.removeEventListener('keydown', onEsc);
          trigger.focus();
        }
        function onEsc(ev) { if (ev.key === 'Escape') closePicker(); }
        document.addEventListener('keydown', onEsc);

        picker.querySelectorAll('[data-vp-close]').forEach(function (el) {
          el.addEventListener('click', closePicker);
        });

        picker.querySelector('[data-vp-add]').addEventListener('click', function () {
          var addBtn = this;
          addBtn.disabled = true;
          addBtn.textContent = 'Toevoegen...';
          fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [{ id: parseInt(select.value, 10), quantity: 1 }] })
          })
            .then(handleAddResponse)
            .then(function () {
              closePicker();
              updateCartUI(true);
              refreshCartDrawer();
              openCartDrawer();
            })
            .catch(function (err) {
              addBtn.disabled = false;
              addBtn.textContent = 'In winkelwagen';
              showCartToast(err && err.handled ? err.message : 'Er ging iets mis bij het toevoegen. Probeer het opnieuw.', true);
            });
        });
      })
      .catch(function () {
        /* Fallback: gewoon naar de productpagina */
        window.location.href = '/products/' + encodeURIComponent(handle);
      });
  }, true);

  /* ========================================
     Aantallen aanpassen (drawer + winkelwagenpagina)
     Eén centrale motor volgens drie principes:
     1. Optimistisch: het getal verandert direct bij elke klik (binnen de
        voorraad-grens), zodat snel doorklikken vloeiend voelt.
     2. Gebundeld: pas als de klant een halve seconde niet meer klikt gaat
        er EEN request naar Shopify met de absolute eindstand van alle
        gewijzigde regels (/cart/update.js, updates op regel-key) plus een
        sections-render in dezelfde request. Absolute aantallen betekenen
        dat de volgorde van kliks nooit uitmaakt: de laatste stand wint.
     3. Geserialiseerd: er loopt nooit meer dan een mutatie tegelijk.
        Klikt de klant tijdens een lopende request door, dan wordt de
        eindstand daarna in een volgende request verstuurd en wordt de
        tussenliggende (verouderde) server-render overgeslagen, zodat het
        scherm nooit terugspringt.
     Totalen dimmen zolang de herberekening loopt (.pp-cart-busy) en tonen
     daarna de door Shopify berekende eindstand. Er wordt nooit een bedrag
     in JS berekend.
     ======================================== */
  var _qtyTargets = {};
  var _qtyFlushTimer = null;
  var _qtyMutationBusy = false;
  var _qtyLastFocus = null;

  function setCartBusy(busy) {
    document.documentElement.classList.toggle('pp-cart-busy', !!busy);
  }

  function queueQtyChange(lineKey, qty, immediate) {
    if (!lineKey) return;
    _qtyTargets[lineKey] = qty;
    setCartBusy(true);
    clearTimeout(_qtyFlushTimer);
    if (immediate) {
      flushQtyChanges();
    } else {
      _qtyFlushTimer = setTimeout(flushQtyChanges, 500);
    }
  }

  function flushQtyChanges() {
    if (_qtyMutationBusy) return; /* na de lopende request volgt vanzelf een nieuwe flush */
    if (!Object.keys(_qtyTargets).length) { setCartBusy(false); return; }
    var updates = _qtyTargets;
    _qtyTargets = {};
    _qtyMutationBusy = true;
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: updates, sections: discountSectionIds().join(',') })
    })
      .then(function (r) {
        if (!r.ok) {
          /* Shopify legt zelf netjes uit waarom (b.v. voorraadlimiet) */
          return r.json().then(function (j) {
            var err = new Error((j && (j.description || j.message)) || 'Bijwerken mislukt. Probeer het opnieuw.');
            err.handled = true; /* nette servermelding, mag getoond worden */
            throw err;
          });
        }
        return r.json();
      })
      .then(function (cart) {
        _qtyMutationBusy = false;
        if (Object.keys(_qtyTargets).length) {
          /* Er is doorgeklikt tijdens de request: deze verouderde render
             overslaan en direct de nieuwe eindstand versturen */
          flushQtyChanges();
          return;
        }
        if (cart.item_count === 0 && document.querySelector('[data-cart-items]')) {
          /* Winkelwagen leeg op de winkelwagenpagina: lay-out verandert volledig */
          window.location.reload();
          return;
        }
        applyCartSections(cart.sections);
        syncDiscountMeta();
        applyCartState(cart);
        setCartBusy(false);
        /* De vervanging haalt de focus weg; zet hem terug op dezelfde knop
           zodat een toetsenbordgebruiker door kan drukken */
        if (_qtyLastFocus && document.activeElement === document.body) {
          var f = document.querySelector(_qtyLastFocus);
          if (f) f.focus();
        }
      })
      .catch(function (err) {
        _qtyMutationBusy = false;
        _qtyTargets = {};
        /* Terug naar de werkelijke serverstand, geen halve optimistische staat.
           Alleen nette servermeldingen letterlijk tonen; rauwe netwerkfouten
           van de browser (b.v. 'Load failed') krijgen een algemene tekst. */
        refreshCartDrawer();
        refreshCartPageItems().then(function () { setCartBusy(false); });
        showCartToast(err && err.handled ? err.message : 'Bijwerken mislukt. Probeer het opnieuw.', true);
      });
  }

  /* Drawer: plus/min-knoppen */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-qty-change]');
    if (!btn) return;
    e.preventDefault();

    var key = btn.dataset.qtyKey;
    var delta = parseInt(btn.dataset.qtyChange, 10);
    var current = parseInt(btn.dataset.qtyCurrent, 10) || 1;
    var next = current + delta;
    if (next < 0) next = 0;

    /* Voorraadmax: niet eens proberen (en dus geen heen-en-weer-gespring) */
    var max = parseInt(btn.dataset.qtyMax, 10);
    if (delta > 0 && max && next > max) {
      showCartToast('Er zijn er helaas maar ' + max + ' op voorraad.', true);
      return;
    }

    var controls = btn.closest('[data-qty-controls]');
    var numEl = controls ? controls.querySelector('.cart-drawer__qty-num') : null;
    var itemEl = btn.closest('.cart-drawer__item');
    if (numEl) numEl.textContent = String(next);
    if (controls) {
      controls.querySelectorAll('[data-qty-change]').forEach(function (b) {
        b.dataset.qtyCurrent = String(next);
      });
    }
    if (itemEl) itemEl.style.opacity = next === 0 ? '0.4' : '';

    _qtyLastFocus = '[data-qty-key="' + key + '"][data-qty-change="' + delta + '"]';
    queueQtyChange(key, next);
  });

  /* Winkelwagenpagina: plus/min-stepper */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-qty-step]');
    if (!btn) return;
    e.preventDefault();
    var wrap = btn.closest('[data-line-qty]');
    var input = wrap ? wrap.querySelector('.cart-page__qty-input') : null;
    if (!wrap || !input) return;

    var delta = parseInt(btn.dataset.qtyStep, 10);
    var next = (parseInt(input.value, 10) || 0) + delta;
    if (next < 0) next = 0;
    var max = parseInt(input.dataset.stock, 10);
    if (delta > 0 && max && next > max) {
      next = max;
      showCartToast('Er zijn er helaas maar ' + max + ' op voorraad.', true);
    }
    input.value = String(next);
    /* laat de voorraad-waarschuwing van de sectie meedraaien */
    input.dispatchEvent(new Event('input', { bubbles: true }));

    _qtyLastFocus = '[data-line-qty="' + wrap.dataset.lineQty + '"] [data-qty-step="' + delta + '"]';
    queueQtyChange(wrap.dataset.lineQty, next);
  });

  /* Winkelwagenpagina: handmatig typen in het aantal-veld */
  document.addEventListener('input', function (e) {
    var input = e.target.closest ? e.target.closest('[data-line-qty] .cart-page__qty-input') : null;
    if (!input) return;
    var wrap = input.closest('[data-line-qty]');
    var val = parseInt(input.value, 10);
    if (isNaN(val) || val < 0) return; /* wachten tot er een geldig getal staat */
    var max = parseInt(input.dataset.stock, 10);
    if (max && val > max) val = max; /* het sectie-script klemt het veld zelf al vast */
    queueQtyChange(wrap.dataset.lineQty, val);
  });

  /* Enter in het aantal-veld: NIET het formulier versturen (de browser zou
     dat als een klik op Afrekenen behandelen, de enige verzendknop), maar
     het aantal direct verwerken. */
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter') return;
    var input = e.target.closest ? e.target.closest('[data-line-qty] .cart-page__qty-input') : null;
    if (!input) return;
    e.preventDefault();
    var wrap = input.closest('[data-line-qty]');
    var val = parseInt(input.value, 10);
    if (isNaN(val) || val < 0) { input.value = ''; return; }
    var max = parseInt(input.dataset.stock, 10);
    if (max && val > max) val = max;
    queueQtyChange(wrap.dataset.lineQty, val, true);
  });

  /* ========================================
     Cart item remove (drawer + winkelwagenpagina)
     Via dezelfde motor (aantal 0, direct versturen) zodat verwijderen
     nooit kan racen met lopende aantal-wijzigingen.
     ======================================== */
  document.addEventListener('click', function (e) {
    var removeBtn = e.target.closest('[data-cart-remove]');
    if (!removeBtn) return;
    e.preventDefault();
    e.stopPropagation();

    var lineKey = removeBtn.dataset.cartRemove;
    var item = removeBtn.closest('.cart-drawer__item') || removeBtn.closest('.cart-page__item');
    if (item) item.style.opacity = '0.4';

    queueQtyChange(lineKey, 0, true);
  });

  /* ========================================
     Kortingscode in de winkelwagen
     Volgens het patroon van Shopify's eigen Horizon-thema:
     - Toepassen/verwijderen gaat via POST /cart/update.js met daarin meteen
       een sections-render: Shopify berekent EN rendert alle bedragen
       server-side in dezelfde request; wij vervangen alleen de HTML.
     - De JSON-response wordt uitsluitend gebruikt voor de geldigheidscheck
       (discount_codes[].applicable) en de naam van de actieve code, nooit
       voor bedragen. Er staat bewust GEEN geld-rekenwerk in deze JS: een
       bedrag dat Shopify zelf rendert kan nooit afwijken van de afrekening.
     - Een AbortController annuleert de vorige aanvraag zodra een nieuwe
       start, zodat een traag oud antwoord nooit een nieuwer antwoord kan
       overschrijven (de oorzaak van wisselende bedragen bij snel klikken).
     Cadeaubonnen kunnen hier niet: die worden pas bij de checkout
     ingewisseld (Shopify-beperking).
     ======================================== */
  var _activeDiscountCode = null;
  var _discountAbort = null;

  function cartPageSectionId() {
    var el = document.querySelector('[data-cart-items]') || document.querySelector('[data-cart-totals]');
    if (!el) return null;
    var sectionEl = el.closest('.shopify-section');
    if (!sectionEl || !sectionEl.id) return null;
    return sectionEl.id.replace(/^shopify-section-/, '');
  }

  function discountSectionIds() {
    var ids = ['cart-drawer'];
    var pageId = cartPageSectionId();
    if (pageId) ids.push(pageId);
    return ids;
  }

  /* Vervangt de drawer en de cart-pagina-delen met de server-gerenderde HTML
     die in dezelfde update-request is meegekomen (response.sections). Eén
     atomaire vervanging uit één consistente winkelwagen-staat. */
  function applyCartSections(sections) {
    if (!sections) return;
    var drawerHtml = sections['cart-drawer'];
    if (drawerHtml && cartDrawer) {
      var ddoc = new DOMParser().parseFromString(drawerHtml, 'text/html');
      var freshDrawer = ddoc.querySelector('[data-cart-drawer]');
      if (freshDrawer) {
        cartDrawer.innerHTML = freshDrawer.innerHTML;
        var newClose = cartDrawer.querySelector('[data-cart-close]');
        if (newClose) newClose.addEventListener('click', closeCartDrawer);
      }
    }
    var pageId = cartPageSectionId();
    var pageHtml = pageId ? sections[pageId] : null;
    if (pageHtml) {
      var pdoc = new DOMParser().parseFromString(pageHtml, 'text/html');
      ['[data-cart-items]', '[data-cart-totals]'].forEach(function (sel) {
        var cur = document.querySelector(sel);
        var freshEl = pdoc.querySelector(sel);
        if (cur && freshEl) cur.innerHTML = freshEl.innerHTML;
      });
    }
  }

  /* Toont/verbergt de invoer en de toegepast-box. Puur metadata (welke code
     is actief), geen bedragen. */
  function syncDiscountMeta(cart) {
    if (cart && cart.discount_codes) {
      _activeDiscountCode = null;
      for (var i = 0; i < cart.discount_codes.length; i++) {
        if (cart.discount_codes[i].applicable) { _activeDiscountCode = cart.discount_codes[i].code; break; }
      }
    }
    document.querySelectorAll('[data-discount-scope]').forEach(function (scope) {
      var details = scope.querySelector('[data-discount-details]');
      var applied = scope.querySelector('[data-discount-applied]');
      var appliedCode = scope.querySelector('[data-discount-applied-code]');
      if (_activeDiscountCode) {
        if (details) { details.open = false; details.hidden = true; }
        if (applied) applied.hidden = false;
        if (appliedCode) appliedCode.textContent = _activeDiscountCode;
      } else {
        if (details) details.hidden = false;
        if (applied) applied.hidden = true;
      }
    });
  }

  /* Na een drawer-vervanging bestaat het oude scope-element niet meer;
     zoek dan het verse exemplaar in de drawer op. */
  function rescopeDiscount(scope) {
    if (scope && scope.isConnected) return scope;
    return cartDrawer ? cartDrawer.querySelector('[data-discount-scope]') : null;
  }

  function setDiscountFeedback(scope, msg, isError) {
    if (!scope) return;
    var fb = scope.querySelector('[data-discount-feedback]');
    if (!fb) return;
    if (!msg) { fb.hidden = true; fb.textContent = ''; fb.classList.remove('is-error'); return; }
    fb.hidden = false;
    fb.textContent = msg;
    fb.classList.toggle('is-error', !!isError);
  }

  /* Eén centrale request voor toepassen en verwijderen: de discount-wijziging
     en de sections-render gebeuren in dezelfde POST (atomair). Een nieuwe
     aanvraag annuleert de vorige. */
  function discountRequest(discountValue) {
    if (_discountAbort) _discountAbort.abort();
    _discountAbort = new AbortController();
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      signal: _discountAbort.signal,
      body: JSON.stringify({ discount: discountValue, sections: discountSectionIds().join(',') })
    }).then(function (r) { return r.json(); });
  }

  function applyDiscountCode(scope, rawCode) {
    var code = (rawCode || '').trim();
    if (!code) { setDiscountFeedback(scope, 'Vul eerst een kortingscode in.', true); return; }
    var btn = scope ? scope.querySelector('[data-discount-apply]') : null;
    if (btn) btn.disabled = true;
    setDiscountFeedback(scope, 'Bezig met controleren...', false);

    discountRequest(code)
      .then(function (cart) {
        var entry = (cart.discount_codes || []).find(function (d) {
          return d.code && d.code.toLowerCase() === code.toLowerCase();
        });
        var discounted = (cart.original_total_price || 0) - (cart.total_price || 0);
        if (entry && entry.applicable && discounted > 0) {
          applyCartSections(cart.sections);
          syncDiscountMeta(cart);
          applyCartState(cart);
          setDiscountFeedback(rescopeDiscount(scope), '', false);
          return;
        }
        /* Ongeldig of levert niets op: niet in de winkelwagen laten hangen
           (een niet-toepasbare code blijft anders in de staat staan en kan
           later onverwacht alsnog actief worden). Herstel de vorige situatie
           en meld het neutraal: Shopify maakt bewust geen onderscheid tussen
           een onbekende en een niet-toepasbare code. */
        return discountRequest(_activeDiscountCode || '').then(function (cart2) {
          applyCartSections(cart2.sections);
          syncDiscountMeta(cart2);
          applyCartState(cart2);
          var s = rescopeDiscount(scope);
          if (s) {
            var details = s.querySelector('[data-discount-details]');
            var input = s.querySelector('[data-discount-input]');
            if (details && !details.hidden) details.open = true;
            if (input) input.value = code;
          }
          setDiscountFeedback(s, 'Deze code is niet geldig of geldt niet voor de artikelen in je winkelwagen.', true);
        });
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        setDiscountFeedback(rescopeDiscount(scope), 'Er ging iets mis. Probeer het zo nog eens.', true);
      })
      .then(function () {
        var s = rescopeDiscount(scope);
        var b = s ? s.querySelector('[data-discount-apply]') : null;
        if (b) b.disabled = false;
        if (btn && btn.isConnected) btn.disabled = false;
      });
  }

  function removeDiscountCode(scope) {
    var btn = scope ? scope.querySelector('[data-discount-remove]') : null;
    if (btn) btn.disabled = true;
    discountRequest('')
      .then(function (cart) {
        applyCartSections(cart.sections);
        syncDiscountMeta(cart);
        applyCartState(cart);
        var s = rescopeDiscount(scope);
        var input = s ? s.querySelector('[data-discount-input]') : null;
        if (input) input.value = '';
        setDiscountFeedback(s, '', false);
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        if (btn && btn.isConnected) btn.disabled = false;
        showCartToast('Verwijderen van de code is niet gelukt. Probeer het opnieuw.', true);
      });
  }

  document.addEventListener('click', function (e) {
    var applyBtn = e.target.closest('[data-discount-apply]');
    if (applyBtn) {
      e.preventDefault();
      var scope = applyBtn.closest('[data-discount-scope]');
      var input = scope ? scope.querySelector('[data-discount-input]') : null;
      applyDiscountCode(scope, input ? input.value : '');
      return;
    }
    var removeBtn = e.target.closest('[data-discount-remove]');
    if (removeBtn) {
      e.preventDefault();
      removeDiscountCode(removeBtn.closest('[data-discount-scope]'));
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter') return;
    var input = e.target.closest('[data-discount-input]');
    if (!input) return;
    e.preventDefault();
    var scope = input.closest('[data-discount-scope]');
    applyDiscountCode(scope, input.value);
  });

  /* Bij het laden: is er al een code actief, toon dan de toegepast-box.
     De bedragen staan al goed (server-side gerenderd bij het laden). */
  fetchCart().then(syncDiscountMeta).catch(function () {});

  /* Kleine publieke API voor sectie-scripts (b.v. cadeau-inpakken in cart.liquid) */
  window.PoppedijnCart = {
    refreshItems: refreshCartPageItems,
    syncDiscount: syncDiscountMeta
  };

  /* ========================================
     Cart recommendations carousel
     ======================================== */
  var recsIndex = 0;
  var recsProducts = [];

  function loadCartRecommendations() {
    var container = document.querySelector('[data-cart-recs]');
    if (!container) return;

    /* Get last cart item to base recommendations on */
    fetchCart()
      .then(function (cart) {
        if (!cart.items || !cart.items.length) {
          container.style.display = 'none';
          return;
        }
        var lastProductId = cart.items[cart.items.length - 1].product_id;
        return fetch('/recommendations/products.json?product_id=' + lastProductId + '&limit=8&intent=related');
      })
      .then(function (r) { return r ? r.json() : null; })
      .then(function (data) {
        if (!data || !data.products || !data.products.length) {
          container.style.display = 'none';
          return;
        }
        recsProducts = data.products;
        recsIndex = 0;
        container.style.display = '';
        renderCartRec(container);
      })
      .catch(function () {});
  }

  function renderCartRec(container) {
    if (!recsProducts.length) return;
    var p = recsProducts[recsIndex];
    var img = p.images && p.images[0] ? p.images[0] : '';
    var imgUrl = typeof img === 'string' ? img : img.src || '';
    if (imgUrl && imgUrl.indexOf('?') === -1) imgUrl += '?width=160';
    var price = '\u20ac' + (p.price / 100).toFixed(2).replace('.', ',');
    var variantId = p.variants && p.variants[0] ? p.variants[0].id : '';

    var display = container.querySelector('[data-cart-recs-display]');
    if (!display) return;

    display.innerHTML =
      '<a href="' + p.url + '" class="cart-rec__product">' +
        (imgUrl ? '<img src="' + escHtml(imgUrl) + '" alt="' + escHtml(p.title) + '" width="80" height="80" loading="lazy" class="cart-rec__img">' : '') +
        '<div class="cart-rec__info">' +
          '<span class="cart-rec__title">' + escHtml(p.title) + '</span>' +
          '<span class="cart-rec__price">' + price + '</span>' +
        '</div>' +
      '</a>' +
      (variantId ? '<button type="button" class="btn btn--primary btn--sm cart-rec__add" data-quick-rec="' + variantId + '">+</button>' : '');

    /* Counter */
    var counter = container.querySelector('[data-cart-recs-counter]');
    if (counter) counter.textContent = (recsIndex + 1) + ' / ' + recsProducts.length;
  }

  /* Navigation & quick-add for cart recommendations */
  document.addEventListener('click', function (e) {
    var container = document.querySelector('[data-cart-recs]');
    if (!container) return;

    if (e.target.closest('[data-cart-recs-prev]')) {
      recsIndex = (recsIndex - 1 + recsProducts.length) % recsProducts.length;
      renderCartRec(container);
    }
    if (e.target.closest('[data-cart-recs-next]')) {
      recsIndex = (recsIndex + 1) % recsProducts.length;
      renderCartRec(container);
    }
    var addBtn = e.target.closest('[data-quick-rec]');
    if (addBtn) {
      e.preventDefault();
      e.stopPropagation();
      var vid = addBtn.dataset.quickRec;
      addBtn.textContent = '...';
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: parseInt(vid, 10), quantity: 1 }] })
      })
        .then(handleAddResponse)
        .then(function () {
          addBtn.textContent = '\u2713';
          updateCartUI(true);
          refreshCartDrawer();
          setTimeout(function () { loadCartRecommendations(); }, 600);
        })
        .catch(function (err) {
          addBtn.textContent = '+';
          showCartToast(err && err.handled ? err.message : 'Er ging iets mis. Probeer het opnieuw.', true);
        });
    }
  });

  /* ========================================
     Sticky header: position:fixed + spacer.
     Announcement bar + mobile search collapse
     via max-height CSS transitions on scroll.
     Because the header is fixed (out of flow),
     child height changes don't cause scroll jitter.
     ======================================== */
  (function () {
    var wrapper = document.querySelector('.shopify-section-group-header-group');
    if (!wrapper || wrapper.querySelector('.header--not-sticky')) return;

    /* Move fixed-position overlays out of wrapper to <body>.
       CSS transform on the wrapper would break their position:fixed otherwise. */
    var overlaySelectors = '[data-mobile-menu], [data-mobile-search-overlay], [data-search-overlay]';
    var overlays = wrapper.querySelectorAll(overlaySelectors);
    Array.prototype.forEach.call(overlays, function (el) { document.body.appendChild(el); });

    /* Create spacer to replace the space the fixed header removes from flow */
    var spacer = document.createElement('div');
    spacer.className = 'header-spacer';
    spacer.setAttribute('aria-hidden', 'true');
    wrapper.after(spacer);

    function measureFull() {
      /* Suppress transitions to avoid visual flash during measurement */
      wrapper.classList.add('no-transition');
      var wasHidden = wrapper.classList.contains('scroll-hidden');
      if (wasHidden) wrapper.classList.remove('scroll-hidden');

      spacer.style.height = Math.ceil(wrapper.getBoundingClientRect().height) + 'px';

      /* Measure announcement bar height for the GPU transform */
      var annEl = wrapper.querySelector('.announcement-bar');
      var annH = annEl ? annEl.offsetHeight : 0;
      wrapper.style.setProperty('--ann-height', annH + 'px');

      /* On mobile, collapse distance includes search bar (reordered above logo via CSS order) */
      var searchEl = wrapper.querySelector('.mobile-search');
      var mobile = window.innerWidth < 990;
      var collapseH = annH + (mobile && searchEl ? searchEl.offsetHeight : 0);
      wrapper.style.setProperty('--collapse-height', collapseH + 'px');

      if (wasHidden) wrapper.classList.add('scroll-hidden');
      wrapper.offsetHeight; /* force reflow before re-enabling transitions */
      wrapper.classList.remove('no-transition');
    }

    measureFull();
    window.addEventListener('resize', measureFull);

    /* Keep spacer in real-time sync for non-scroll height changes (e.g. cart reminder).
       Guarded by isScrollToggle to ignore wrapper height changes during scroll transitions. */
    var isScrollToggle = false;
    var scrollTimer;

    if (window.ResizeObserver) {
      new ResizeObserver(function () {
        if (!isScrollToggle && !wrapper.classList.contains('scroll-hidden')) {
          spacer.style.height = Math.ceil(wrapper.getBoundingClientRect().height) + 'px';
        }
      }).observe(wrapper);
    }

    /* Scroll handling with delta threshold - prevents rapid toggling */
    var lastY = window.scrollY;
    var cumDelta = 0;
    var hidden = false;
    var ticking = false;
    var isMobile = window.innerWidth < 990;
    var DELTA = isMobile ? 50 : 15;      /* mobile needs bigger threshold (momentum scroll) */
    var MIN_Y = isMobile ? 120 : 80;     /* don't hide until scrolled this far */
    var COOLDOWN = isMobile ? 400 : 0;   /* ms between toggles on mobile */
    var lastToggle = 0;

    /* Recalculate on resize (orientation change) */
    window.addEventListener('resize', function () {
      isMobile = window.innerWidth < 990;
      DELTA = isMobile ? 50 : 15;
      MIN_Y = isMobile ? 120 : 80;
      COOLDOWN = isMobile ? 400 : 0;
    });

    function onScroll() {
      var y = window.scrollY;
      var d = y - lastY;

      /* Don't toggle header state while a mega-menu is open */
      if (wrapper.querySelector('.mega-menu.is-open')) {
        lastY = y;
        ticking = false;
        return;
      }
      var now = Date.now();
      var cooledDown = (now - lastToggle) >= COOLDOWN;

      /* Reset accumulated delta when direction reverses */
      if ((d > 0 && cumDelta < 0) || (d < 0 && cumDelta > 0)) cumDelta = 0;
      cumDelta += d;

      if (!hidden && cumDelta > DELTA && y > MIN_Y && cooledDown) {
        wrapper.classList.add('scroll-hidden');
        hidden = true;
        cumDelta = 0;
        lastToggle = now;
        isScrollToggle = true;
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(function () { isScrollToggle = false; }, 450);
      } else if (hidden && (cumDelta < -DELTA || y <= 0) && cooledDown) {
        wrapper.classList.remove('scroll-hidden');
        hidden = false;
        cumDelta = 0;
        lastToggle = now;
        isScrollToggle = true;
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(function () {
          isScrollToggle = false;
          /* After scroll-up transition ends, sync spacer to current full height */
          spacer.style.height = Math.ceil(wrapper.getBoundingClientRect().height) + 'px';
        }, 450);
      }

      lastY = y;
      ticking = false;
    }

    window.addEventListener('scroll', function () {
      if (!ticking) { requestAnimationFrame(onScroll); ticking = true; }
    }, { passive: true });
  })();

  /* ========================================
     Cart reminder bar (abandoned cart)
     Tekst-updates lopen centraal via applyCartState.
     Hier alleen: tonen voor terugkerende bezoekers
     en wegklik-gedrag.
     ======================================== */
  (function () {
    var bar = document.querySelector('[data-cart-reminder]');
    if (!bar) return;
    var closeBtn = bar.querySelector('[data-cart-reminder-close]');

    /* Check if dismissed recently (within 30 minutes) */
    var dismissedAt = null;
    try { dismissedAt = localStorage.getItem('cart_reminder_dismissed_at'); } catch (e) { /* private mode */ }
    if (dismissedAt && (Date.now() - parseInt(dismissedAt, 10)) < 30 * 60 * 1000) return;

    /* Only show when returning (not first page load of session) */
    var hasVisited;
    try { hasVisited = sessionStorage.getItem('cart_reminder_visited'); } catch (e) { hasVisited = null; }
    if (!hasVisited) {
      try { sessionStorage.setItem('cart_reminder_visited', '1'); } catch (e) { /* private mode */ }
      return;
    }

    /* Eerst verse cart-stand ophalen, dan pas tonen.
       Voorkomt dat een verouderde server-side telling getoond wordt. */
    setTimeout(function () {
      fetchCart()
        .then(function (cart) {
          applyCartState(cart);
          if (cart.item_count > 0 && !bar.classList.contains('is-hidden')) {
            bar.classList.add('is-visible');
          }
        })
        .catch(function () { /* offline: liever geen reminder dan een foute */ });
    }, 1000);

    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        bar.classList.remove('is-visible');
        bar.classList.add('is-hidden');
        try { localStorage.setItem('cart_reminder_dismissed_at', String(Date.now())); } catch (e) { /* private mode */ }
      });
    }
  })();

  /* ========================================
     Sale countdown timers (per-product, deterministic)
     ======================================== */
  (function () {

    function formatTime(ms) {
      var totalSec = Math.floor(ms / 1000);
      var d = Math.floor(totalSec / 86400);
      var h = Math.floor((totalSec % 86400) / 3600);
      var m = Math.floor((totalSec % 3600) / 60);
      var s = totalSec % 60;
      var pad = function (n) { return n < 10 ? '0' + n : '' + n; };
      if (d > 0) return d + (d === 1 ? ' dag ' : ' dagen ') + pad(h) + ':' + pad(m) + ':' + pad(s);
      if (h > 0) return h + ':' + pad(m) + ':' + pad(s);
      return m + ':' + pad(s);
    }

    function updateAll() {
      var els = document.querySelectorAll('[data-sale-countdown][data-sale-ends-at]');
      els.forEach(function (el) {
        var endsAt = el.dataset.saleEndsAt;
        if (!endsAt) return;
        var remaining = new Date(endsAt).getTime() - Date.now();
        var text = el.querySelector('[data-countdown-text]');
        if (remaining <= 0) {
          if (text) text.textContent = 'Actie is verlopen';
          el.style.opacity = '0.5';
          return;
        }
        if (text) text.textContent = 'Actie verloopt over ' + formatTime(remaining);
      });
    }

    /* Alleen een interval als er echt een einddatum-countdown op de pagina staat */
    if (document.querySelector('[data-sale-countdown][data-sale-ends-at]')) {
      updateAll();
      setInterval(updateAll, 1000);
    }
  })();

  /* ========================================
     Desktop inline search with predictive dropdown
     ======================================== */
  (function () {
    var input = document.querySelector('[data-desktop-search-input]');
    var dropdown = document.querySelector('[data-desktop-search-dropdown]');
    if (!input || !dropdown) return;

    var debounceTimer = null;
    var isOpen = false;

    function openDropdown() {
      if (isOpen) return;
      dropdown.classList.add('is-open');
      isOpen = true;
    }

    function closeDropdown() {
      dropdown.classList.remove('is-open');
      isOpen = false;
    }

    function escHtmlD(s) {
      var d = document.createElement('div');
      d.textContent = s;
      return d.innerHTML;
    }

    function showDropdownResults(q) {
      dropdown.innerHTML = '<div style="text-align:center;padding:var(--space-lg);"><div class="ps-loading__spinner" style="margin:0 auto;"></div></div>';
      openDropdown();

      fetch('/search/suggest.json?q=' + encodeURIComponent(q) + '&resources[type]=product,collection,page&resources[limit]=6')
        .then(function (r) { return r.json(); })
        .then(function (data) {
          var res = data.resources && data.resources.results;
          if (!res) { showDropdownEmpty(q); return; }

          var products = res.products || [];
          var collections = res.collections || [];
          var total = products.length + collections.length;
          if (total === 0) { showDropdownEmpty(q); return; }

          var html = '';

          if (products.length) {
            html += '<div class="ps-section"><div class="ps-section__title">Producten</div><div class="ps-products">';
            products.forEach(function (p) {
              var img = p.image || (p.featured_image && p.featured_image.url) || '';
              if (img && img.indexOf('?') === -1) img += '?width=120';
              var price = p.price ? ('\u20AC' + parseFloat(p.price).toFixed(2).replace('.', ',')) : '';
              html += '<a href="' + p.url + '" class="ps-product">';
              html += '<div class="ps-product__image">';
              if (img) html += '<img src="' + escHtmlD(img) + '" width="56" height="56" loading="lazy">';
              html += '</div>';
              html += '<div class="ps-product__info">';
              if (p.vendor) html += '<span class="ps-product__vendor">' + escHtmlD(p.vendor) + '</span>';
              html += '<span class="ps-product__title">' + escHtmlD(p.title) + '</span>';
              if (price) html += '<span class="ps-product__price">' + price + '</span>';
              html += '</div></a>';
            });
            html += '</div></div>';
          }

          if (collections.length) {
            html += '<div class="ps-section"><div class="ps-section__title">Categorie\u00ebn</div>';
            collections.forEach(function (c) {
              html += '<a href="' + c.url + '" class="ps-collection">';
              html += '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>';
              html += escHtmlD(c.title) + '</a>';
            });
            html += '</div>';
          }

          html += '<a href="/search?q=' + encodeURIComponent(q) + '" class="ps-view-all">Alle resultaten bekijken <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"/></svg></a>';

          dropdown.innerHTML = html;
        })
        .catch(function () { showDropdownEmpty(q); });
    }

    function showDropdownEmpty(q) {
      dropdown.innerHTML = '<div class="ps-empty"><p>Geen resultaten voor \u201C' + escHtmlD(q) + '\u201D</p><span>Probeer een andere zoekterm</span></div>';
    }

    input.addEventListener('focus', function () {
      if (input.value.trim().length >= 2) {
        openDropdown();
      }
    });

    input.addEventListener('input', function () {
      clearTimeout(debounceTimer);
      var q = input.value.trim();
      if (q.length < 2) { closeDropdown(); return; }
      debounceTimer = setTimeout(function () { showDropdownResults(q); }, 250);
    });

    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        var q = input.value.trim();
        if (q) window.location.href = '/search?q=' + encodeURIComponent(q);
      }
      if (e.key === 'Escape') { closeDropdown(); input.blur(); }
    });

    /* Prevent form submit - use JS navigation instead */
    var form = input.closest('form');
    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var q = input.value.trim();
        if (q) window.location.href = '/search?q=' + encodeURIComponent(q);
      });
    }

    /* Close on click outside */
    document.addEventListener('click', function (e) {
      if (isOpen && !e.target.closest('.header__search')) {
        closeDropdown();
      }
    });
  })();

  /* ========================================
     Mobile search overlay with live results
     ======================================== */
  (function () {
    var trigger = document.querySelector('.mobile-search__input');
    var overlay = document.querySelector('[data-mobile-search-overlay]');
    var input = document.querySelector('[data-mobile-search-input]');
    var closeBtn = document.querySelector('[data-mobile-search-close]');
    var resultsBody = document.querySelector('[data-mobile-search-results]');
    if (!overlay || !input) return;

    var debounceTimer = null;

    function openMobileSearch() {
      overlay.classList.add('is-open');
      lockBodyScroll();
      input.focus();
    }

    function closeMobileSearch() {
      overlay.classList.remove('is-open');
      unlockBodyScroll();
      input.value = '';
      if (resultsBody) resultsBody.innerHTML = '<p style="color: var(--color-foreground-muted); text-align: center; padding-top: var(--space-xl);">Typ om te zoeken...</p>';
    }

    /* Tap on the static search bar opens fullscreen overlay */
    if (trigger) {
      trigger.addEventListener('focus', function (e) {
        e.preventDefault();
        trigger.blur();
        openMobileSearch();
      });
    }

    /* Magnifying glass button also opens overlay */
    var searchTriggerBtn = document.querySelector('[data-mobile-search-trigger]');
    if (searchTriggerBtn) {
      searchTriggerBtn.addEventListener('click', function (e) {
        e.preventDefault();
        openMobileSearch();
      });
    }

    /* Prevent form submit on mobile search bar */
    var mobileForm = document.querySelector('.mobile-search__form');
    if (mobileForm) {
      mobileForm.addEventListener('submit', function (e) {
        e.preventDefault();
        openMobileSearch();
      });
    }

    if (closeBtn) closeBtn.addEventListener('click', closeMobileSearch);

    /* Live predictive search */
    input.addEventListener('input', function () {
      clearTimeout(debounceTimer);
      var q = input.value.trim();
      if (q.length < 2) {
        if (resultsBody) resultsBody.innerHTML = '<p style="color: var(--color-foreground-muted); text-align: center; padding-top: var(--space-xl);">Typ om te zoeken...</p>';
        return;
      }
      debounceTimer = setTimeout(function () { fetchMobileResults(q); }, 250);
    });

    /* Submit on Enter */
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        var q = input.value.trim();
        if (q) window.location.href = '/search?q=' + encodeURIComponent(q);
      }
    });

    /* Close on Escape */
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeMobileSearch();
    });

    function fetchMobileResults(q) {
      if (resultsBody) resultsBody.innerHTML = '<div style="text-align:center;padding:var(--space-xl);"><div class="ps-loading__spinner" style="margin:0 auto;"></div></div>';

      fetch('/search/suggest.json?q=' + encodeURIComponent(q) + '&resources[type]=product,collection,page&resources[limit]=8')
        .then(function (r) { return r.json(); })
        .then(function (data) {
          var res = data.resources && data.resources.results;
          if (!res) { showEmpty(q); return; }

          var products = res.products || [];
          var collections = res.collections || [];
          var pages = res.pages || [];
          var total = products.length + collections.length + pages.length;
          if (total === 0) { showEmpty(q); return; }

          var html = '';

          if (products.length) {
            html += '<div style="margin-bottom:var(--space-lg);">';
            products.forEach(function (p) {
              var img = p.image || (p.featured_image && p.featured_image.url) || '';
              if (img && img.indexOf('?') === -1) img += '?width=120';
              var price = p.price ? ('\u20AC' + parseFloat(p.price).toFixed(2).replace('.', ',')) : '';
              html += '<a href="' + p.url + '" style="display:flex;align-items:center;gap:0.75rem;padding:0.5rem 0;text-decoration:none;color:inherit;border-bottom:1px solid var(--color-border);">';
              if (img) html += '<img src="' + escHtml(img) + '" width="56" height="56" style="border-radius:var(--border-radius-sm);object-fit:cover;background:var(--color-surface);flex-shrink:0;" loading="lazy">';
              html += '<div style="min-width:0;flex:1;"><div style="font-weight:500;font-size:0.875rem;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">' + escHtml(p.title) + '</div>';
              if (price) html += '<div style="font-size:0.8125rem;font-weight:600;color:var(--color-foreground);">' + price + '</div>';
              html += '</div></a>';
            });
            html += '</div>';
          }

          if (collections.length) {
            html += '<div style="margin-bottom:var(--space-md);">';
            html += '<div style="font-size:0.6875rem;font-weight:700;text-transform:uppercase;letter-spacing:0.1em;color:var(--color-foreground-muted);margin-bottom:var(--space-xs);">Categorie\u00ebn</div>';
            collections.forEach(function (c) {
              html += '<a href="' + c.url + '" style="display:block;padding:0.375rem 0;text-decoration:none;color:var(--color-foreground);font-size:0.875rem;font-weight:500;">' + escHtml(c.title) + '</a>';
            });
            html += '</div>';
          }

          html += '<a href="/search?q=' + encodeURIComponent(q) + '" style="display:block;text-align:center;padding:var(--space-md);margin-top:var(--space-sm);font-weight:600;font-size:0.8125rem;color:var(--color-primary);text-decoration:none;">Alle resultaten bekijken &rarr;</a>';

          if (resultsBody) resultsBody.innerHTML = html;
        })
        .catch(function () {
          showEmpty(q);
        });
    }

    function showEmpty(q) {
      if (resultsBody) resultsBody.innerHTML = '<div style="text-align:center;padding:var(--space-xl);color:var(--color-foreground-muted);"><p style="font-size:0.9375rem;">Geen resultaten voor \u201C' + escHtml(q) + '\u201D</p></div>';
    }
  })();

  /* ========================================
     Scroll Hints - "Fairy Dust"
     Magical scroll indicators for horizontal-scroll sections.
     Add data-scroll-hint to any overflow-x container.
     ======================================== */
  (function () {
    var css = [
      '.scroll-hint-wrap { position: relative; }',
      '.scroll-hint-edge {',
      '  position: absolute; top: 0; bottom: 0; width: 56px;',
      '  pointer-events: none; z-index: 3;',
      '  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 4px;',
      '  transition: opacity 0.45s ease;',
      '}',
      '.scroll-hint-edge--right { right: 0; }',
      '.scroll-hint-edge--left  { left: 0; }',

      /* sparkle dots */
      '.scroll-hint-spark {',
      '  width: 5px; height: 5px; border-radius: 50%;',
      '  background: var(--color-primary, #5B7C5A);',
      '  opacity: 0; animation: shSparkle 2.8s ease-in-out infinite;',
      '}',
      '.scroll-hint-spark:nth-child(1) { width: 4px; height: 4px; animation-delay: 0s; }',
      '.scroll-hint-spark:nth-child(2) { width: 7px; height: 7px; animation-delay: 0.5s; }',
      '.scroll-hint-spark:nth-child(3) { width: 5px; height: 5px; animation-delay: 1.1s; }',

      /* chevron */
      '.scroll-hint-chevron {',
      '  font-size: 1.4rem; line-height: 1; font-weight: 300;',
      '  color: var(--color-primary, #5B7C5A); opacity: 0.55;',
      '  animation: shBounceR 1.6s ease-in-out infinite;',
      '}',
      '.scroll-hint-edge--left .scroll-hint-chevron { animation-name: shBounceL; }',

      /* initial attention pulse - runs once */
      '.scroll-hint-edge--intro { animation: shIntro 2.2s ease; }',

      /* keyframes */
      '@keyframes shSparkle {',
      '  0%, 100% { opacity: 0; transform: translateY(0) scale(0.7); }',
      '  30% { opacity: 0.85; transform: translateY(-6px) scale(1.2); }',
      '  60% { opacity: 0.5; transform: translateY(2px) scale(1); }',
      '}',
      '@keyframes shBounceR {',
      '  0%, 100% { transform: translateX(0); }',
      '  50% { transform: translateX(5px); }',
      '}',
      '@keyframes shBounceL {',
      '  0%, 100% { transform: translateX(0); }',
      '  50% { transform: translateX(-5px); }',
      '}',
      '@keyframes shIntro {',
      '  0%   { opacity: 0; transform: translateX(12px); }',
      '  15%  { opacity: 1; transform: translateX(0); }',
      '  30%  { opacity: 1; transform: translateX(3px); }',
      '  45%  { opacity: 1; transform: translateX(0); }',
      '  100% { opacity: 1; transform: translateX(0); }',
      '}'
    ].join('\n');

    var styleEl = document.createElement('style');
    styleEl.textContent = css;
    document.head.appendChild(styleEl);

    function nearestBg(el) {
      var cur = el;
      while (cur && cur !== document.documentElement) {
        var bg = getComputedStyle(cur).backgroundColor;
        if (bg && bg !== 'transparent' && bg !== 'rgba(0, 0, 0, 0)') return bg;
        cur = cur.parentElement;
      }
      return '#ffffff';
    }

    function createEdge(side, bg) {
      var edge = document.createElement('div');
      edge.className = 'scroll-hint-edge scroll-hint-edge--' + side;
      edge.style.opacity = '0';
      var gradDir = side === 'right' ? 'to left' : 'to right';
      edge.style.background = 'linear-gradient(' + gradDir + ', ' + bg + ' 18%, transparent)';
      for (var i = 0; i < 3; i++) {
        var s = document.createElement('span');
        s.className = 'scroll-hint-spark';
        edge.appendChild(s);
      }
      var ch = document.createElement('span');
      ch.className = 'scroll-hint-chevron';
      ch.textContent = side === 'right' ? '\u203A' : '\u2039';
      edge.appendChild(ch);
      return edge;
    }

    function initHints() {
      var targets = document.querySelectorAll('[data-scroll-hint]');
      targets.forEach(function (el) {
        if (el.dataset.shInit) return;
        el.dataset.shInit = '1';

        var parent = el.parentElement;
        if (!parent) return;
        parent.style.position = 'relative';

        var bg = nearestBg(parent);
        var right = createEdge('right', bg);
        var left  = createEdge('left', bg);
        parent.appendChild(right);
        parent.appendChild(left);

        function update() {
          /* If the element isn't actually scrollable (overflow: visible on desktop), hide both */
          var ov = getComputedStyle(el).overflowX;
          if (ov !== 'auto' && ov !== 'scroll') {
            right.style.opacity = '0';
            left.style.opacity = '0';
            return;
          }
          var sl = el.scrollLeft;
          var max = el.scrollWidth - el.clientWidth;
          /* Use padding as threshold so scroll-snap offset within padding doesn't trigger edge */
          var padL = parseInt(getComputedStyle(el).paddingLeft) || 0;
          var padR = parseInt(getComputedStyle(el).paddingRight) || 0;
          var threshold = Math.max(padL, padR, 8);
          if (max <= threshold) {
            right.style.opacity = '0';
            left.style.opacity = '0';
            return;
          }
          right.style.opacity = (sl < max - threshold) ? '1' : '0';
          left.style.opacity = (sl > threshold) ? '1' : '0';
        }

        /* Debounced re-check: catches scroll-snap settling after momentum scroll */
        var scrollTimer;
        el.addEventListener('scroll', function () {
          update();
          clearTimeout(scrollTimer);
          scrollTimer = setTimeout(update, 150);
        }, { passive: true });
        /* scrollend fires after snap completes (modern browsers) */
        el.addEventListener('scrollend', update);
        window.addEventListener('resize', update);
        update();

        /* Re-check when images inside the container finish loading
           (scrollWidth may change after lazy images render) */
        el.querySelectorAll('img').forEach(function (img) {
          if (!img.complete) img.addEventListener('load', update, { once: true });
        });

        /* ResizeObserver catches any content size change (fonts, dynamic content) */
        if (window.ResizeObserver) {
          new ResizeObserver(update).observe(el);
        }

        /* Safety net: re-check after short delay for late-rendering content */
        setTimeout(update, 500);

        /* Intro pulse on right edge if actually scrollable */
        var ovInit = getComputedStyle(el).overflowX;
        if ((ovInit === 'auto' || ovInit === 'scroll') && el.scrollWidth > el.clientWidth + 5) {
          right.classList.add('scroll-hint-edge--intro');
          right.addEventListener('animationend', function () {
            right.classList.remove('scroll-hint-edge--intro');
          }, { once: true });
        }
      });
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initHints);
    } else {
      initHints();
    }
    document.addEventListener('shopify:section:load', initHints);
  })();

  /* ========================================
     Init
     ======================================== */
  updateCartUI();
})();
